import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { type Observable } from 'rxjs'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { useIsElementInView } from '@/hooks/element-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { useObservable } from '@/hooks/rx-hooks'
import styles from './Step.module.css'

export interface StepProps {
  title: string
  descriptionHtml?: string
  children?: ReactNode
  isLast?: boolean
  index?: number
  active?: boolean
  isNotValid?: boolean
  becomeActive$: Observable<unknown>
  onBecomeVisible: () => unknown
  onBecomeHidden: () => unknown
  compact?: boolean
  testId?: string
}

/**
 * A step of the draft form.
 */
export function Step({
  title,
  descriptionHtml,
  children,
  isLast,
  index,
  active,
  isNotValid,
  becomeActive$,
  onBecomeHidden,
  onBecomeVisible,
  compact,
  testId,
}: StepProps) {
  const [showingDescription, setShowingDescription] = useState(false)
  const toggleDescription = useCallback(
    () => setShowingDescription((v) => !v),
    [],
  )
  const stepRef = useRef<HTMLDivElement | null>(null)
  const scrollIntoView = useCallback(
    () => stepRef.current?.scrollIntoView({ block: 'center' }),
    [],
  )

  const { connectRef } = useIsElementInView((isInView) =>
    isInView ? onBecomeVisible() : onBecomeHidden(),
  )
  const composedRef = useComposedRefs(stepRef, connectRef)

  useObservable(becomeActive$, scrollIntoView)

  // Only the step that is active on mount scrolls here; later activations go
  // through becomeActive$.
  const [isActiveOnMount] = useState(active)
  useEffect(() => {
    if (isActiveOnMount) {
      scrollIntoView()
    }
  }, [isActiveOnMount, scrollIntoView])

  return (
    <div className={cx(styles.root, active && styles.active)} ref={composedRef}>
      {index !== undefined && (
        <div className={styles.gutter}>
          <div className={styles.number}>{index + 1}</div>
          {!isLast && <div className={styles.line} />}
        </div>
      )}
      <div className={cx(styles.main, compact && styles.compact)}>
        <div>
          <h2
            className={cx(
              styles.title,
              compact && styles.compact,
              isNotValid && styles.isNotValid,
            )}
            data-test={`${testId}-title`}
          >
            {title}
            {descriptionHtml && (
              <RawButton
                className={cx(
                  styles.descriptionToggle,
                  isNotValid && styles.isNotValid,
                )}
                onPress={toggleDescription}
                testId={`${testId}-description-toggle`}
              >
                <Icon auto name="info" />
              </RawButton>
            )}
          </h2>
          {descriptionHtml && showingDescription && (
            <div
              className={cx(
                styles.description,
                compact && styles.compact,
                isNotValid && styles.isNotValid,
              )}
              data-test={`${testId}-description`}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          )}
        </div>
        {children && (
          <div className={cx(styles.content, compact && styles.compact)}>
            {children}
          </div>
        )}
      </div>
    </div>
  )
}
